import {ICountingGameOptions, IEvent, IGame, ITimedGameOptions, IUserModification} from "@webng-types/write-model";
import React from "react";

export type SportDisplayTimeFormat = 'MM:ss' | 'M:ss' | 'M\'' | 'M.' | 'M' | ''
export type SportDisplayPhaseFormat = 'long' | 'short' | ''
export type SportDisplayPhaseOvertimeFormat = 'overtime' | 'extra-time'

export interface SportstypeSpec {
  id: string
  scoreboardType?: ScoreboardType
  showScorers?: boolean
  svg: React.VFC<React.SVGProps<SVGSVGElement>>,
  releaseStage: SportReleaseStage,
  defaultTiming?: ITimedGameOptions | ICountingGameOptions
  displayTimeFormat?: SportDisplayTimeFormat
  displayPhaseFormat?: SportDisplayPhaseFormat
  displayPhaseOvertimeFormat?: SportDisplayPhaseOvertimeFormat
  events: SportstypeEventSpec[],
  legacyEventMap?: Record<number, string|undefined>,
  isFastSportstype?: boolean
  timingLabelVariant?: SportTimingLabelVariant
  /**
   * Explicit allow-list of base phase ids (matching `SportPhaseSpec.id` in phases.ts — the
   * unexpanded id, e.g. `set` rather than `set.1`; a repeating id allows every one of its
   * expanded/break instances) valid for this sportstype's manual phase picker. Only needed for
   * sportstypes without a periodic `defaultTiming` (their valid phases can't be derived from
   * `num_regular_phases`/`num_overtime_phases`) — periodic sportstypes derive their phases
   * instead and can leave this unset.
   *
   * A repeating phase's `repeats` in phases.ts is shared by every sportstype allow-listing it, set
   * high enough for whichever sportstype needs the most (e.g. `set`'s 21 covers darts/table
   * tennis) — wrong for sportstypes that need fewer (tennis tops out at 5 sets). An entry can be
   * `{id, max}` instead of a plain id to cap it to that sportstype's own realistic maximum.
   */
  phases?: SportstypePhaseAllowListEntry[]
}

export type SportstypePhaseAllowListEntry = string | {id: string, max: number}

/**
 * Selects the i18n namespace for the editor's time-of-play and phase labels
 * (`editor.event.sport.<variant>.*`). Left unset, a sportstype keeps the default match-clock
 * wording ("Spielzeit", "z. B. 4'"), which is correct for the sixteen specs that carry no
 * `defaultTiming` but are still played over a clock.
 *
 * Deliberately an opt-in rather than something derived from `defaultTiming` being absent: having no
 * configured default timing is not the same as having no clock, and deriving it would silently
 * relabel every clockless-by-configuration sport to fix the ones that are clockless by nature.
 *
 * `freeform`: the time is whatever the operator types — an alpine split or finish time ("+0.34"),
 * not a match minute.
 */
export type SportTimingLabelVariant = 'freeform'

export type ScoreboardType = 'team-game-scoreboard' | 'set-based-scoreboard' | 'baseball-scoreboard' | 'gaelic-scoreboard'
export type SportEventTeamSelectSpec = 'any'|'none'|'home'|'guest'
export type SportEventPlayerSelectSpec = 'selected-team'|'other-team'|'any'|'none'|'home'|'guest'
export type SportEventPlayerId = "scorer"|"assist"|"shooter"|"keeper"|"injured"|"offender"|"in"|"out"|"rider"|"driver"|"up"|"down"|"leader"|"second"|"skier"|"podium-1"|"podium-2"|"podium-3"
export type SportReleaseStage = 'alpha' | 'beta' | 'released'

export enum TeamGameScoreboardDisplayTypes {
  DEFAULT = "default",
  DEFAULT_LOGO = "default_logo",
  DEFAULT_TEXT = "default_text",
  TABLE = "table",
  TABLE_TEXT = "table_text"
}

export enum SetBasedScoreboardDisplayTypes {
  DEFAULT = "default",
  DEFAULT_ONLY_SCORES = "default_only_scores",
  THUMB = "thumb",
  THUMB_ONLY_SCORES = "thumb_only_scores"
}

export interface SportEventPlayerSpec {
  id: SportEventPlayerId
  from?: SportEventPlayerSelectSpec
  max?: number
  svg?: React.VFC<React.SVGProps<SVGSVGElement>>
}

export interface SportstypeEventSpec {
  id: string
  important: boolean
  svg: React.VFC<React.SVGProps<SVGSVGElement> & { cancelled?: boolean }>
  team?: SportEventTeamSelectSpec
  players?: SportEventPlayerSpec[]
  scoreChange?: number
  scoreChangeOpponent?: number,
  eventHandler?: (game: IGame, event: IEvent, isNewEvent: boolean) => IUserModification[],
  cancellable?: boolean
}

export interface SportPhaseTimingSpec {

}

export interface SportPhaseSpec {
  id: string
  repeats?: number
  hasBreaks?: boolean
}

export interface SportMetadata {
  sportstypes: SportstypeSpec[]
  phases: SportPhaseSpec[]
}
